import {t} from '@/i18n'
import Idea from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import {titleOfInstance} from '@/utils/ontosUtils'
import {instanceColor} from '@/utils/colors'

/**
 * Os cabeçalhos das telas de design e de item, num lugar só.
 *
 * A regra: no título **a coisa**; no subtítulo **o que se faz com ela**. Criando ainda
 * não há coisa, e o título passa a ser **onde ela vai nascer** — a coleção, para uma
 * propriedade ou um item; o ambiente, para uma coleção ou um fluxo.
 *
 * Mora fora das telas porque quase toda ação daqui tem um par página/modal — editar um
 * item, criar um item, criar um fluxo, criar uma coleção — e os dois lados precisam
 * dizer a mesma coisa. Antes cada um montava o seu, e eles foram se soltando: metade
 * estava com a ação no título e o nome embaixo, o inverso da outra metade.
 *
 * Os parâmetros pedem só o que o cabeçalho lê, e não os tipos inteiros: assim um util
 * não puxa a cadeia de imports do workspace enriquecido.
 *
 * `t()` dentro da função, e nunca no corpo do módulo: traduzir no carregamento
 * congelaria o idioma no que estivesse ativo naquele instante.
 */

/**
 * O `accent` é a cor da COISA de que a tela trata, quando ela tem uma — hoje, o item.
 *
 * Entra pelo cabeçalho porque é aqui que página e modal já concordam: as quatro superfícies de
 * um item (ver e editar, em página e em modal) recebem este mesmo objeto. Uma tela que não fala
 * de coisa colorida simplesmente não o preenche.
 */
export type Heading = { title: string; subtitle: string; accent?: string }

type Named = { name: string }
type Titled = { title: string }

export function workspaceDesign(workspace: Titled): Heading {
    return {title: workspace.title, subtitle: t('workspace-design')}
}

export function workspaceEdit(workspace: Titled): Heading {
    return {title: workspace.title, subtitle: t('workspace-edit')}
}

export function workspaceRoles(workspace: Titled): Heading {
    return {title: workspace.title, subtitle: t('roles-edit')}
}

export function collectionDesign(idea: Named): Heading {
    return {title: idea.name, subtitle: t('collection-design')}
}

export function collectionCreate(workspace: Titled): Heading {
    return {title: workspace.title, subtitle: t('collection-create')}
}

export function collectionEdit(idea: Named): Heading {
    return {title: idea.name, subtitle: t('collection-edit')}
}

export function collectionAccess(idea: Named): Heading {
    return {title: idea.name, subtitle: t('access.edit')}
}

export function workflowDesign(workflow: Named): Heading {
    return {title: workflow.name, subtitle: t('workflow-design')}
}

export function domainDesign(domain: Named): Heading {
    return {title: domain.name, subtitle: t('domain-design')}
}

export function domainCreate(workspace: Titled): Heading {
    return {title: workspace.title, subtitle: t('domain-create')}
}

export function workflowCreate(workspace: Titled): Heading {
    return {title: workspace.title, subtitle: t('workflow-create')}
}

export function propertyCreate(idea: Named): Heading {
    return {title: idea.name, subtitle: t('property-new')}
}

/**
 * `Coisa · Coisa dentro dela` — o ponto médio é o separador de "coisa dentro de coisa" em todo
 * o app, e não um enfeite deste título: é o mesmo do identificador composto e do caminho das
 * seções da ficha. Ponto médio e não hífen, que é sinal de palavra composta e se confunde com
 * o que está DENTRO de cada parte.
 *
 * Existe como função porque são dois cabeçalhos a compor — a propriedade em edição e o título
 * do quadro, que ganha o nome da dimensão quando as raias não são a situação — e escrito à mão
 * nos dois o espaçamento em volta do ponto divergiria.
 *
 * Parte vazia não deixa separador solto: quem compõe não precisa testar antes de chamar.
 */
export function within(...parts: Array<string | undefined>): string {
    return parts.filter(part => !!part).join(' · ')
}

export function propertyEdit(idea: Named, property: IdeaProperty): Heading {
    return {title: within(idea.name, property.name), subtitle: t('property-edit')}
}

export function instanceCreate(idea: Named): Heading {
    return {title: idea.name, subtitle: t('new')}
}

type AnyInstance = { _identifier?: string; _instance?: number }

/**
 * A cor do item, para o acento da janela — ausente enquanto ele não tem id.
 *
 * É o caso de criar: coisa que ainda não nasceu não tem id e, portanto, não tem cor. A regra
 * cai sozinha do modelo, sem um `if` sobre qual tela está aberta.
 *
 * O id também chega de FORA, e não só de dentro do objeto, porque o objeto carregado pode não
 * trazê-lo: a ficha pede ao servidor `meta: ["rels", "identifier", "state"]`, sem `instance` —
 * o id já estava na rota e ninguém precisava dele no documento. Quem abre a tela sempre sabe
 * qual item é, e é ele quem completa.
 */
function accentOf(instance: AnyInstance, id?: number | string | null): string | undefined {
    const instanceId = instance._instance ?? (id === undefined || id === null ? undefined : Number(id))
    return instanceId === undefined || Number.isNaN(instanceId) ? undefined : instanceColor(instanceId)
}

/** A ficha: o item em cima, a coleção a que ele pertence embaixo. */
export function instanceView(instance: AnyInstance, idea: Idea, id?: number | string | null): Heading {
    return {
        title: titleOfInstance(instance, idea),
        subtitle: idea.name,
        accent: accentOf(instance, id)
    }
}

export function instanceEdit(instance: AnyInstance, idea: Idea, id?: number | string | null): Heading {
    return {
        title: titleOfInstance(instance, idea),
        subtitle: t('instance-edit', {collection: idea.name}) as string,
        accent: accentOf(instance, id)
    }
}
